// apps/web/src/components/detail/VulnContextStrip.tsx
import { SeverityBadge } from '@/components/shared/SeverityBadge'
import { PreviouslyKevFlaggedPill } from '@/components/vulnerabilities/PreviouslyKevFlaggedPill'
import { EpssChip } from '@/components/vulnerabilities/EpssChip'
import { isV4EpssChipEnabled } from '@/lib/flags'
import { formatDate } from '@/lib/utils'
import type { Vulnerability } from '@/lib/types'
import type { VendorDisplay } from '@/lib/vendor-display'

export interface VulnContextStripProps {
  vuln: Vulnerability
  /**
   * Resolved vendor list from the fallback chain (Phase B → supply-chain
   * → advisory). The strip surfaces only the first entry as a small
   * `Vendor:` line so the tile is no longer "vendorless" for CVEs where
   * Phase B's `affected_vendors uuid[]` is empty but other vendor signals
   * exist on the same page. Optional — when omitted or empty the line
   * hides cleanly (back-compat with any caller still rendering the strip
   * without the resolved list, e.g. visual-regression fixtures).
   */
  displayVendors?: VendorDisplay[]
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

export function VulnContextStrip({
  vuln,
  displayVendors,
}: VulnContextStripProps) {
  const topVendor = displayVendors && displayVendors.length > 0
    ? displayVendors[0]
    : null
  const cvss = vuln.cvssScore ? parseFloat(vuln.cvssScore).toFixed(1) : null
  // Legacy inline EPSS rendering (flag OFF path). V4 Slice 3 swap uses EpssChip
  // when VITE_VULNS_V4_EPSS_CHIP_ENABLED is "true".
  const epss = vuln.epssScore
    ? (parseFloat(vuln.epssScore) * 100).toFixed(1) + '%'
    : null
  const useEpssChip = isV4EpssChipEnabled()

  const affectedCount = Array.isArray(vuln.affectedProducts)
    ? vuln.affectedProducts.length
    : vuln.affectedProducts && typeof vuln.affectedProducts === 'object'
    ? Object.keys(vuln.affectedProducts as Record<string, unknown>).length
    : 0

  return (
    <div className="flex flex-col gap-4 rounded-md border p-4">
      <div className="font-mono text-2xl font-semibold">{vuln.cveId}</div>

      <div className="flex flex-wrap items-center gap-2">
        {vuln.severity && <SeverityBadge severity={vuln.severity} />}
        <PreviouslyKevFlaggedPill
          previouslyKevFlagged={vuln.previouslyKevFlagged ?? false}
          exploitationStatus={vuln.exploitationStatus}
          kevRemovedAt={vuln.kevRemovedAt}
          kevFirstSeenAt={vuln.kevFirstSeenAt}
        />
      </div>

      {topVendor && (
        <div
          className="flex flex-wrap items-baseline gap-2 text-sm"
          data-testid="vuln-context-vendor-line"
        >
          <span className="text-xs uppercase text-muted-foreground">Vendor</span>
          <span className="font-medium">{topVendor.name}</span>
          {topVendor.source !== 'phase-b' && (
            <span
              className="text-[10px] uppercase tracking-wide text-muted-foreground"
              data-testid={`vuln-context-vendor-provenance-${topVendor.source}`}
            >
              {topVendor.source === 'supply-chain'
                ? 'from supply-chain'
                : 'from advisory'}
            </span>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-4 text-sm">
        {cvss && (
          <div className="flex flex-col">
            <span className="text-xs uppercase text-muted-foreground">CVSS</span>
            <span className="font-medium">{cvss}</span>
          </div>
        )}
        {useEpssChip ? (
          <EpssChip
            epssScore={vuln.epssScore ?? null}
            epssPercentile={vuln.epssPercentile ?? null}
            epssRefreshedAt={vuln.epssRefreshedAt ?? null}
          />
        ) : (
          epss && (
            <div className="flex flex-col">
              <span className="text-xs uppercase text-muted-foreground">EPSS</span>
              <span className="font-medium">{epss}</span>
            </div>
          )
        )}
        {vuln.exploitationStatus && (
          <div className="flex flex-col">
            <span className="text-xs uppercase text-muted-foreground">Exploitation</span>
            <span className="font-medium">{capitalize(vuln.exploitationStatus)}</span>
          </div>
        )}
      </div>

      {vuln.patchAvailable === true && (
        <div className="text-sm font-medium text-emerald-600">Patch available</div>
      )}
      {vuln.patchAvailable === false && (
        <div className="text-sm font-medium text-red-600">No patch</div>
      )}

      {(vuln.firstSeen || vuln.lastUpdated) && (
        <div className="grid grid-cols-2 gap-4 text-sm">
          {vuln.firstSeen && (
            <div className="flex flex-col">
              <span className="text-xs uppercase text-muted-foreground">First seen</span>
              <span>{formatDate(vuln.firstSeen)}</span>
            </div>
          )}
          {vuln.lastUpdated && (
            <div className="flex flex-col">
              <span className="text-xs uppercase text-muted-foreground">Last updated</span>
              <span>{formatDate(vuln.lastUpdated)}</span>
            </div>
          )}
        </div>
      )}

      {affectedCount > 0 && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-xs uppercase text-muted-foreground">Affected products</span>
          <span className="font-medium">{affectedCount}</span>
        </div>
      )}
    </div>
  )
}
